import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, 
  Sun, 
  ListChecks, 
  Moon, 
  BarChart3, 
  Settings as SettingsIcon,
  Droplets,
  Utensils,
  Zap,
  Heart,
  Target,
  Scale,
  Smile,
  Check,
  Sparkles,
  User,
  Shirt,
  Rocket,
  Clock,
  Dumbbell,
  BookOpen,
  Flame,
  Plus,
  TreePine,
  Brain,
  Users,
  Trash2,
  Pencil,
  X
} from "lucide-react";

// Hooks
import { useHabitData } from "./hooks/useHabitData";
import { useReminders } from "./hooks/useReminders";

// Components
import { ProgressRing } from "./components/ProgressRing";
import { WaterTracker } from "./components/WaterTracker";
import { TodoList } from "./components/TodoList";
import { MorningIntention } from "./components/MorningIntention";
import { DailyHabits } from "./components/DailyHabits";
import { EveningReflection } from "./components/EveningReflection";
import { FoodLog } from "./components/FoodLog";
import { ProgressStats } from "./components/ProgressStats";
import { Settings } from "./components/Settings";
import { Reflections } from "./components/Reflections";

// Hyphen8 Logo
const HYPHEN8_LOGO = "https://customer-assets.emergentagent.com/job_serene-routine-5/artifacts/0c3kdwbu_png-2.png";

const DAILY_QUOTES = [
  "The quieter you become, the more you can hear.",
  "Simplicity is the ultimate sophistication.",
  "Be still. The answers will come.",
  "Small daily improvements are the key to staggering long-term results.",
  "The way you start your day sets the tone for everything.",
  "Peace comes from within. Do not seek it without.",
  "Progress, not perfection.",
  "What you do today matters.",
  "Breathe. You are exactly where you need to be.",
  "One step at a time.",
  "Your calm mind is the ultimate weapon.",
  "Embrace the pace of your own journey.",
  "Let go of what you cannot control.",
  "Today is a gift. Unwrap it gently.",
  "The present moment is all you ever have.",
  "Trust the process.",
  "You are enough.",
  "Begin with intention. End with gratitude.",
  "Clarity comes from action, not thought.",
  "Do less, but do it better."
];

const READY_QUOTES = [
  "You've set yourself up for success. Now go make it happen.",
  "The morning is won. The day is yours.",
  "You showed up for yourself. That's the hardest part.",
  "Prepared mind, prepared day. Go conquer.",
  "You're ready. Trust yourself.",
  "The foundation is set. Build something great today.",
];

const COMPLETION_QUOTES = [
  "You did it. You showed up, followed through, and finished strong.",
  "Rest well tonight. You've earned it.",
  "Another day of choosing yourself. That's not small.",
  "Consistency compounds. You're building something real.",
  "The work is done. Let go and recharge.",
  "You kept your promises to yourself today. That matters.",
];

const getReadyQuote = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return READY_QUOTES[dayOfYear % READY_QUOTES.length];
};

const getCompletionQuote = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return COMPLETION_QUOTES[dayOfYear % COMPLETION_QUOTES.length];
};

const getDailyQuote = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return DAILY_QUOTES[dayOfYear % DAILY_QUOTES.length];
};

const SLEEP_OPTIONS = [
  { value: 'great', label: 'Well', sublabel: 'Feeling rested' },
  { value: 'notbad', label: 'Could use more', sublabel: 'Functioning' },
  { value: 'bad', label: 'Shitty', sublabel: 'Rough night' },
  { value: 'didnt', label: "I didn't", sublabel: "Running on fumes" }
];

const Dashboard = () => {
  const { 
    todayData, 
    updateToday, 
    calculateStreak, 
    calculateCompletionPercentage,
    getLast7Days,
    settings,
    setSettings
  } = useHabitData();

  const { requestNotificationPermission } = useReminders(settings, todayData);

  const streak = calculateStreak();
  const completion = calculateCompletionPercentage();
  const last7Days = getLast7Days();

  const userName = settings.userName || '';
  const needsName = !userName;

  const step1Complete = Boolean(todayData.sleepQuality);
  const step2Complete = Boolean(todayData.feelingSet);
  const step3Complete = Boolean(todayData.gratitudeComplete && todayData.gratitude?.trim().length > 0);
  const step4Complete = Boolean(todayData.intentionComplete && todayData.whatWillMakeTodayGreat?.trim().length > 0);
  const step5Complete = Boolean(todayData.nonNegotiables?.length === 3);
  const morningPromptsComplete = step1Complete && step2Complete && step3Complete && step4Complete && step5Complete;
  
  const isMorningRoutineComplete = Boolean(
    todayData.skinCareAM &&
    todayData.brushedTeethAM &&
    todayData.gotReady &&
    todayData.dressed &&
    todayData.readyToGo
  );

  // Track if user has seen the "ready" quote after completing morning routine
  const [hasSeenReadyQuote, setHasSeenReadyQuote] = React.useState(false);
  
  // Check if all daily habits are complete for the celebration
  const allHabitsComplete = completion === 100;
  const [hasSeenCompletionCelebration, setHasSeenCompletionCelebration] = React.useState(todayData.seenCompletionCelebration || false);

  const handleSleepSelect = (value) => {
    const now = new Date();
    const wakeTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    updateToday({ 
      sleepQuality: value,
      wakeUpTime: wakeTime,
      wakeUpTimestamp: now.toISOString()
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCurrentStep = () => {
    if (needsName) return 0;
    if (!step1Complete) return 1;
    if (!step2Complete) return 2;
    if (!step3Complete) return 3;
    if (!step4Complete) return 4;
    if (!step5Complete) return 5;
    if (!isMorningRoutineComplete) return 6;
    if (!hasSeenReadyQuote) return 7; // Ready quote screen
    return 8; // Idle dashboard
  };

  const currentStep = getCurrentStep();

  const [newNonNeg, setNewNonNeg] = React.useState('');
  const addNonNegotiable = () => {
    if (!newNonNeg.trim() || (todayData.nonNegotiables?.length || 0) >= 3) return;
    const updated = [...(todayData.nonNegotiables || []), {
      id: Date.now(),
      text: newNonNeg.trim(),
      completed: false
    }];
    updateToday({ nonNegotiables: updated });
    setNewNonNeg('');
  };

  const [newTask, setNewTask] = React.useState('');
  const addTask = () => {
    if (!newTask.trim()) return;
    const updated = [...(todayData.additionalTasks || []), {
      id: Date.now(),
      text: newTask.trim(),
      completed: false
    }];
    updateToday({ additionalTasks: updated });
    setNewTask('');
  };

  const toggleNonNeg = (id) => {
    const updated = todayData.nonNegotiables.map(t => 
      t.id === id ? { ...t, completed: !t.completed } : t
    );
    updateToday({ nonNegotiables: updated });
  };

  const toggleTask = (id) => {
    const updated = todayData.additionalTasks.map(t => 
      t.id === id ? { ...t, completed: !t.completed } : t
    );
    updateToday({ additionalTasks: updated });
  };

  // Delete functions
  const deleteNonNeg = (id) => {
    const updated = todayData.nonNegotiables.filter(t => t.id !== id);
    updateToday({ nonNegotiables: updated });
  };

  const deleteTask = (id) => {
    const updated = todayData.additionalTasks.filter(t => t.id !== id);
    updateToday({ additionalTasks: updated });
  };

  // Edit state for non-negotiables and tasks
  const [editingNonNeg, setEditingNonNeg] = React.useState(null);
  const [editingTask, setEditingTask] = React.useState(null);
  const [editText, setEditText] = React.useState('');

  const startEditNonNeg = (item) => {
    setEditingNonNeg(item.id);
    setEditText(item.text);
  };

  const saveEditNonNeg = (id) => {
    if (!editText.trim()) return;
    const updated = todayData.nonNegotiables.map(t => 
      t.id === id ? { ...t, text: editText.trim() } : t
    );
    updateToday({ nonNegotiables: updated });
    setEditingNonNeg(null);
    setEditText('');
  };

  const startEditTask = (task) => {
    setEditingTask(task.id);
    setEditText(task.text);
  };

  const saveEditTask = (id) => {
    if (!editText.trim()) return;
    const updated = todayData.additionalTasks.map(t => 
      t.id === id ? { ...t, text: editText.trim() } : t
    );
    updateToday({ additionalTasks: updated });
    setEditingTask(null);
    setEditText('');
  };

  // Food logging state
  const [foodInput, setFoodInput] = React.useState('');
  const [portionAmount, setPortionAmount] = React.useState('1');
  const [portionType, setPortionType] = React.useState('cup');
  const [isEstimating, setIsEstimating] = React.useState(false);
  const [showFoodInput, setShowFoodInput] = React.useState(false);

  const PORTION_TYPES = ['oz', 'lbs', 'cup', 'tablespoon', 'teaspoon', 'piece', 'slice', 'serving', 'bowl', 'plate'];

  const addFoodItem = async () => {
    if (!foodInput.trim()) return;
    setIsEstimating(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/estimate-calories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          food: foodInput.trim(),
          portion: 'serving',
          portion_amount: 1
        })
      });
      
      const data = await response.json();
      
      const newFoodItem = {
        id: Date.now(),
        food: foodInput.trim(),
        calories: data.calories,
        protein: data.protein,
        carbs: data.carbs,
        fat: data.fat
      };
      
      const updatedFoodLog = [...(todayData.foodLog || []), newFoodItem];
      updateToday({ foodLog: updatedFoodLog });
      
      setFoodInput('');
      setShowFoodInput(false);
    } catch (error) {
      console.error('Error estimating calories:', error);
      // Add with estimated calories on error
      const newFoodItem = {
        id: Date.now(),
        food: foodInput.trim(),
        calories: 150,
        protein: null,
        carbs: null,
        fat: null
      };
      const updatedFoodLog = [...(todayData.foodLog || []), newFoodItem];
      updateToday({ foodLog: updatedFoodLog });
      setFoodInput('');
      setShowFoodInput(false);
    }
    setIsEstimating(false);
  };

  const deleteFoodItem = (id) => {
    const updatedFoodLog = (todayData.foodLog || []).filter(item => item.id !== id);
    updateToday({ foodLog: updatedFoodLog });
  };

  const totalCalories = (todayData.foodLog || []).reduce((sum, item) => sum + (item.calories || 0), 0);
  const calorieGoal = settings.calorieGoal || 2000;
  const calorieStatus = totalCalories > calorieGoal ? 'over' : totalCalories < calorieGoal ? 'under' : 'at';

  // Sage color for accents
  const sage = '#8a9a8a';
  const sageBg = 'rgba(138, 154, 138, 0.1)';
  const sageLight = 'rgba(138, 154, 138, 0.6)';

  return (
    <div className="min-h-screen pb-28 md:pb-8" data-testid="dashboard">
      
      {/* STEP 0: NAME CAPTURE */}
      {currentStep === 0 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="name-step">
          <div className="text-center max-w-sm mx-auto w-full">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-12" style={{ backgroundColor: sageBg }}>
              <Sun className="w-7 h-7" style={{ color: sage }} />
            </div>
            
            <h1 className="text-4xl text-white/90 mb-3">
              Welcome
            </h1>
            <p className="text-white/30 mb-16 font-light">
              Your space for intention
            </p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-white/30 text-xs mb-3 tracking-widest uppercase">
                  Your name
                </label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  className="input-futuristic w-full text-center text-lg"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      setSettings(prev => ({ ...prev, userName: e.target.value.trim() }));
                    }
                  }}
                  data-testid="name-input"
                />
              </div>
              <button
                onClick={(e) => {
                  const input = document.querySelector('[data-testid="name-input"]');
                  if (input.value.trim()) {
                    setSettings(prev => ({ ...prev, userName: input.value.trim() }));
                  }
                }}
                className="btn-primary w-full"
                data-testid="name-continue-btn"
              >
                Begin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 1: HOW DID YOU SLEEP */}
      {currentStep === 1 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="sleep-step">
          <div className="text-center max-w-md mx-auto">
            <p className="text-white/20 text-xs tracking-widest uppercase mb-6">
              {formatDate()}
            </p>
            
            <h1 className="text-4xl text-white/90 mb-2">
              {getGreeting()}, {userName}
            </h1>
            <p className="text-white/30 mb-12 font-light">
              How did you sleep?
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {SLEEP_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSleepSelect(option.value)}
                  className="glass-card p-6 hover:bg-white/[0.05] transition-all duration-500"
                  data-testid={`sleep-${option.value}`}
                >
                  <span className="text-white/80 font-medium block mb-1">{option.label}</span>
                  <span className="text-white/20 text-xs font-light">{option.sublabel}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: HOW ARE YOU FEELING */}
      {currentStep === 2 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="feeling-step">
          <div className="text-center max-w-sm mx-auto w-full">
            <h2 className="text-3xl text-white/90 mb-2">
              How are you feeling?
            </h2>
            <p className="text-white/30 font-light mb-12">
              Rate your energy right now
            </p>
            
            <div className="glass-card p-10">
              <div className="flex justify-center items-baseline mb-4">
                <span className="text-6xl font-light" style={{ color: sage }}>{todayData.feeling || 5}</span>
                <span className="text-white/20 text-2xl ml-1">/10</span>
              </div>
              
              <p className="text-white/40 text-sm mb-6">
                {(todayData.feeling || 5) <= 3 ? 'Running low' : (todayData.feeling || 5) <= 6 ? 'Getting by' : 'Feeling good'}
              </p>
              
              {/* Slider with visible track */}
              <div className="relative mb-8 py-4">
                <div className="flex justify-between text-xs text-white/30 mb-3">
                  <span>Low</span>
                  <span>High</span>
                </div>
                <div className="relative h-8 flex items-center">
                  <div className="absolute inset-x-0 h-2 bg-white/[0.08] rounded-full"></div>
                  <div 
                    className="absolute h-2 rounded-full transition-all duration-200"
                    style={{ 
                      width: `${((todayData.feeling || 5) - 1) / 9 * 100}%`,
                      backgroundColor: sage
                    }}
                  ></div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={todayData.feeling || 5}
                    onChange={(e) => updateToday({ feeling: parseInt(e.target.value) })}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                    data-testid="feeling-slider"
                  />
                  <div 
                    className="absolute w-6 h-6 rounded-full border-2 border-white/20 transition-all duration-200 pointer-events-none"
                    style={{ 
                      left: `calc(${((todayData.feeling || 5) - 1) / 9 * 100}% - 12px)`,
                      backgroundColor: sage
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-white/20 mt-3">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <span key={n} className={n === (todayData.feeling || 5) ? 'text-white/60' : ''}>{n}</span>
                  ))}
                </div>
              </div>
              
              <textarea
                value={todayData.feelingWhy || ''}
                onChange={(e) => updateToday({ feelingWhy: e.target.value })}
                placeholder="What's on your mind? (optional)"
                className="input-futuristic w-full min-h-20 resize-none text-sm mb-8"
                data-testid="feeling-why-input"
              />
              
              <button
                onClick={() => updateToday({ feelingSet: true })}
                className="btn-primary w-full"
                data-testid="feeling-continue-btn"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: GRATITUDE */}
      {currentStep === 3 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="gratitude-step">
          <div className="text-center max-w-sm mx-auto w-full">
            <h2 className="text-3xl text-white/90 mb-2">
              Gratitude
            </h2>
            <p className="text-white/30 font-light mb-12">
              What are you thankful for today?
            </p>
            
            <div className="glass-card p-10">
              <textarea
                value={todayData.gratitude || ''}
                onChange={(e) => updateToday({ gratitude: e.target.value })}
                placeholder="I'm grateful for..."
                className="input-futuristic w-full min-h-36 resize-none mb-8"
                data-testid="gratitude-input"
              />
              
              <button
                onClick={() => updateToday({ gratitudeComplete: true })}
                disabled={!todayData.gratitude?.trim().length}
                className={`btn-primary w-full ${!todayData.gratitude?.trim().length ? 'opacity-30' : ''}`}
                data-testid="gratitude-continue-btn"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: INTENTION */}
      {currentStep === 4 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="today-great-step">
          <div className="text-center max-w-sm mx-auto w-full">
            <h2 className="text-3xl text-white/90 mb-2">
              Intention
            </h2>
            <p className="text-white/30 font-light mb-12">
              What will make today meaningful?
            </p>
            
            <div className="glass-card p-10">
              <textarea
                value={todayData.whatWillMakeTodayGreat || ''}
                onChange={(e) => updateToday({ whatWillMakeTodayGreat: e.target.value })}
                placeholder="Today I intend to..."
                className="input-futuristic w-full min-h-36 resize-none mb-8"
                data-testid="today-great-input"
              />
              
              <button
                onClick={() => updateToday({ intentionComplete: true })}
                disabled={!todayData.whatWillMakeTodayGreat?.trim().length}
                className={`btn-primary w-full ${!todayData.whatWillMakeTodayGreat?.trim().length ? 'opacity-30' : ''}`}
                data-testid="today-great-continue-btn"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 5: NON-NEGOTIABLES */}
      {currentStep === 5 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 animate-fade-in" data-testid="nonneg-step">
          <div className="text-center max-w-sm mx-auto w-full">
            <h2 className="text-3xl text-white/90 mb-2">
              Non-negotiables
            </h2>
            <p className="text-white/30 font-light mb-12">
              What are 3 things you have to get done today?
            </p>
            
            <div className="glass-card p-8">
              <div className="space-y-3 mb-6">
                {(todayData.nonNegotiables || []).map((item, index) => (
                  <div 
                    key={item.id}
                    className="flex items-center gap-4 p-4 rounded-xl"
                    style={{ backgroundColor: sageBg }}
                  >
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-sm" style={{ backgroundColor: 'rgba(138, 154, 138, 0.3)', color: sage }}>
                      {index + 1}
                    </span>
                    <span className="flex-1 text-left text-white/80">{item.text}</span>
                  </div>
                ))}
                
                {Array.from({ length: 3 - (todayData.nonNegotiables?.length || 0) }).map((_, i) => (
                  <div 
                    key={`empty-${i}`}
                    className="flex items-center gap-4 p-4 rounded-xl border border-dashed border-white/[0.06]"
                  >
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-sm bg-white/[0.03] text-white/20">
                      {(todayData.nonNegotiables?.length || 0) + i + 1}
                    </span>
                    <span className="flex-1 text-left text-white/15 font-light">
                      {(todayData.nonNegotiables?.length || 0) + i === 0 ? "What's been on your list?" : "Priority " + ((todayData.nonNegotiables?.length || 0) + i + 1)}
                    </span>
                  </div>
                ))}
              </div>
              
              {(todayData.nonNegotiables?.length || 0) < 3 && (
                <div className="flex gap-3 mb-6">
                  <input
                    value={newNonNeg}
                    onChange={(e) => setNewNonNeg(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addNonNegotiable()}
                    placeholder="Things I've been putting off..."
                    className="input-futuristic flex-1"
                    data-testid="nonneg-input"
                  />
                  <button
                    onClick={addNonNegotiable}
                    disabled={!newNonNeg.trim()}
                    className="w-12 h-12 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] flex items-center justify-center transition-all disabled:opacity-30"
                    style={{ color: sage }}
                    data-testid="add-nonneg-btn"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              )}
              
              <p className="text-white/20 text-sm font-light">
                {(todayData.nonNegotiables?.length || 0)} of 3
              </p>
            </div>
          </div>
        </div>
      )}

      {/* STEP 6: QUOTE + MORNING ROUTINE */}
      {currentStep === 6 && (
        <div className="min-h-screen px-6 py-16 animate-fade-in" data-testid="routine-step">
          <div className="max-w-sm mx-auto">
            {/* Quote */}
            <section className="text-center mb-16">
              <p className="text-xl text-white/60 italic leading-relaxed font-light">
                "{getDailyQuote()}"
              </p>
              {todayData.wakeUpTime && (
                <p className="text-white/20 text-sm mt-8 font-light">
                  {todayData.wakeUpTime}
                </p>
              )}
            </section>

            {/* Morning Check Off */}
            <section className="glass-card p-8">
              <h3 className="text-lg text-white/60 mb-8 text-center font-light tracking-wide">Morning check off</h3>
              
              <div className="space-y-2">
                {[
                  { key: 'skinCareAM', label: 'Skincare' },
                  { key: 'brushedTeethAM', label: 'Brush teeth' },
                  { key: 'gotReady', label: 'Get ready' },
                  { key: 'dressed', label: 'Get dressed' },
                  { key: 'readyToGo', label: 'Ready to go' },
                ].map((item, index) => (
                  <button
                    key={item.key}
                    onClick={() => updateToday({ [item.key]: !todayData[item.key] })}
                    className={`flex items-center gap-4 p-4 rounded-xl w-full transition-all duration-500 ${
                      todayData[item.key]
                        ? ''
                        : 'hover:bg-white/[0.02]'
                    }`}
                    style={{
                      backgroundColor: todayData[item.key] ? sageBg : 'transparent'
                    }}
                    data-testid={`routine-${item.key}`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500"
                      style={{
                        backgroundColor: todayData[item.key] ? sage : 'rgba(255,255,255,0.03)',
                        color: todayData[item.key] ? '#0d0c0b' : 'rgba(255,255,255,0.2)'
                      }}
                    >
                      {todayData[item.key] ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <span className="text-xs">{index + 1}</span>
                      )}
                    </div>
                    <span className={`font-light transition-colors duration-500`} style={{
                      color: todayData[item.key] ? sage : 'rgba(255,255,255,0.5)'
                    }}>
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        </div>
      )}

      {/* STEP 7: READY QUOTE - Motivational transition */}
      {currentStep === 7 && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="ready-quote-step">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-12" style={{ backgroundColor: sageBg }}>
              <Rocket className="w-7 h-7" style={{ color: sage }} />
            </div>
            
            <p className="text-2xl text-white/70 italic leading-relaxed font-light mb-12">
              "{getReadyQuote()}"
            </p>
            
            <button
              onClick={() => setHasSeenReadyQuote(true)}
              className="btn-primary px-12"
              data-testid="ready-continue-btn"
            >
              Let's go
            </button>
          </div>
        </div>
      )}

      {/* STEP 8: IDLE MODE */}
      {currentStep === 8 && (
        <div data-testid="idle-dashboard">
          <header className="pt-12 pb-10 px-6">
            <div className="max-w-lg mx-auto">
              <p className="text-white/20 text-xs tracking-widest uppercase mb-3">
                {formatDate()}
              </p>
              <h1 className="text-3xl text-white/90">
                {getGreeting()}, {userName}
              </h1>
              {todayData.wakeUpTime && (
                <p className="text-white/20 text-sm mt-2 font-light">
                  Up since {todayData.wakeUpTime}
                </p>
              )}
            </div>
          </header>

          <main className="px-6 space-y-8 max-w-lg mx-auto">
            
            {/* NON-NEGOTIABLES - Bold at top */}
            <section className="glass-card p-8" style={{ borderColor: 'rgba(138, 154, 138, 0.2)' }} data-testid="nonneg-section">
              <h3 className="text-sm text-white/50 mb-6 tracking-widest uppercase font-medium">Non-negotiables</h3>
              
              <div className="space-y-2">
                {(todayData.nonNegotiables || []).map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-4 p-5 rounded-xl transition-all duration-300`}
                    style={{
                      backgroundColor: item.completed ? 'rgba(138, 154, 138, 0.08)' : 'rgba(255,255,255,0.03)'
                    }}
                    data-testid={`nonneg-${item.id}`}
                  >
                    {editingNonNeg === item.id ? (
                      <>
                        <input
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveEditNonNeg(item.id)}
                          className="input-futuristic flex-1 text-lg"
                          autoFocus
                          data-testid={`edit-nonneg-${item.id}`}
                        />
                        <button
                          onClick={() => saveEditNonNeg(item.id)}
                          className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
                          style={{ color: sage }}
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => { setEditingNonNeg(null); setEditText(''); }}
                          className="p-2 rounded-lg hover:bg-white/[0.05] text-white/30 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleNonNeg(item.id)}
                          className="w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0"
                          style={{
                            backgroundColor: item.completed ? sage : 'transparent',
                            border: item.completed ? 'none' : '2px solid rgba(138, 154, 138, 0.4)'
                          }}
                        >
                          {item.completed && <Check className="w-4 h-4" style={{ color: '#0d0c0b' }} />}
                        </button>
                        <span 
                          className={`flex-1 text-left text-lg transition-all cursor-pointer ${
                            item.completed ? 'text-white/30 line-through' : 'text-white font-medium'
                          }`}
                          onClick={() => toggleNonNeg(item.id)}
                        >
                          {item.text}
                        </span>
                        <button
                          onClick={() => startEditNonNeg(item)}
                          className="p-2 rounded-lg hover:bg-white/[0.05] text-white/20 hover:text-white/50 transition-colors opacity-0 group-hover:opacity-100"
                          style={{ opacity: 1 }}
                          data-testid={`edit-btn-nonneg-${item.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteNonNeg(item.id)}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-white/20 hover:text-red-400 transition-colors"
                          data-testid={`delete-btn-nonneg-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* ADDITIONAL TASKS */}
            <section className="glass-card p-8" data-testid="tasks-section">
              <h3 className="text-sm text-white/30 mb-6 tracking-widest uppercase">Tasks</h3>
              
              <div className="flex gap-3 mb-6">
                <input
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTask()}
                  placeholder="Things I've been putting off..."
                  className="input-futuristic flex-1"
                  data-testid="new-task-input"
                />
                <button
                  onClick={addTask}
                  disabled={!newTask.trim()}
                  className="w-12 h-12 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] text-white/30 flex items-center justify-center transition-all disabled:opacity-30"
                  data-testid="add-task-btn"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-1">
                {(todayData.additionalTasks || []).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/[0.02] transition-all"
                    data-testid={`task-${task.id}`}
                  >
                    {editingTask === task.id ? (
                      <>
                        <input
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveEditTask(task.id)}
                          className="input-futuristic flex-1 text-sm"
                          autoFocus
                          data-testid={`edit-task-${task.id}`}
                        />
                        <button
                          onClick={() => saveEditTask(task.id)}
                          className="p-1.5 rounded-lg hover:bg-white/[0.05] transition-colors"
                          style={{ color: sage }}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingTask(null); setEditText(''); }}
                          className="p-1.5 rounded-lg hover:bg-white/[0.05] text-white/30 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleTask(task.id)}
                          className="w-5 h-5 rounded flex items-center justify-center transition-all flex-shrink-0"
                          style={{
                            backgroundColor: task.completed ? sage : 'transparent',
                            border: task.completed ? 'none' : '1px solid rgba(255,255,255,0.1)'
                          }}
                        >
                          {task.completed && <Check className="w-3 h-3" style={{ color: '#0d0c0b' }} />}
                        </button>
                        <span 
                          className={`flex-1 text-left text-sm font-light cursor-pointer ${
                            task.completed ? 'text-white/20 line-through' : 'text-white/50'
                          }`}
                          onClick={() => toggleTask(task.id)}
                        >
                          {task.text}
                        </span>
                        <button
                          onClick={() => startEditTask(task)}
                          className="p-1.5 rounded-lg hover:bg-white/[0.05] text-white/20 hover:text-white/50 transition-colors"
                          data-testid={`edit-btn-task-${task.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/20 hover:text-red-400 transition-colors"
                          data-testid={`delete-btn-task-${task.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* PRODUCTIVITY & GROWTH */}
            <section className="glass-card p-8" data-testid="productivity-section">
              <h3 className="text-sm text-white/30 mb-6 tracking-widest uppercase">Productivity & Growth</h3>
              
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'moneyMove', label: 'Made a money move', icon: Rocket },
                  { key: 'practicedSkill', label: 'Practiced a skill', icon: BookOpen },
                  { key: 'decluttered', label: 'Decluttered', icon: Trash2 },
                  { key: 'connectedWithSomeone', label: 'Connected', icon: Users },
                  { key: 'gym', label: 'Worked out', icon: Dumbbell },
                  { key: 'nature', label: 'Went outside', icon: TreePine },
                ].map((habit) => (
                  <button
                    key={habit.key}
                    onClick={() => updateToday({ [habit.key]: !todayData[habit.key] })}
                    className="flex items-center gap-3 p-4 rounded-xl transition-all duration-300"
                    style={{
                      backgroundColor: todayData[habit.key] ? sageBg : 'rgba(255,255,255,0.02)'
                    }}
                    data-testid={`habit-${habit.key}`}
                  >
                    <habit.icon className="w-4 h-4" style={{ color: todayData[habit.key] ? sage : 'rgba(255,255,255,0.2)' }} />
                    <span className="text-xs font-light" style={{ color: todayData[habit.key] ? sage : 'rgba(255,255,255,0.3)' }}>
                      {habit.label}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            {/* TRACKING - Water & Reading */}
            <section className="grid grid-cols-2 gap-4">
              <div className="glass-card p-6" data-testid="water-section">
                <div className="flex items-center gap-2 mb-4">
                  <Droplets className="w-4 h-4 text-white/20" />
                  <span className="text-white/30 text-xs tracking-widest uppercase">Water</span>
                </div>
                <div className="text-center mb-4">
                  <span className="text-3xl font-light" style={{ color: sage }}>{todayData.waterOunces || 0}</span>
                  <span className="text-white/20 text-sm">oz</span>
                </div>
                <div className="flex gap-1">
                  {[8, 16, 32].map((oz) => (
                    <button
                      key={oz}
                      onClick={() => updateToday({ waterOunces: (todayData.waterOunces || 0) + oz })}
                      className="flex-1 py-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] text-white/30 text-xs transition-all"
                      data-testid={`water-add-${oz}`}
                    >
                      +{oz}
                    </button>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="reading-section">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-white/20" />
                  <span className="text-white/30 text-xs tracking-widest uppercase">Reading</span>
                </div>
                <div className="text-center mb-2">
                  <span className="text-3xl font-light" style={{ color: sage }}>{todayData.readPages || 0}</span>
                  <span className="text-white/20 text-sm"> pages</span>
                </div>
                {todayData.bookPagesLeft && (
                  <p className="text-center text-white/20 text-xs mb-2">{todayData.bookPagesLeft} left</p>
                )}
                <div className="flex gap-1">
                  {[5, 10, 20].map((pg) => (
                    <button
                      key={pg}
                      onClick={() => updateToday({ readPages: (todayData.readPages || 0) + pg })}
                      className="flex-1 py-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] text-white/30 text-xs transition-all"
                      data-testid={`reading-add-${pg}`}
                    >
                      +{pg}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* FOOD LOG */}
            <section className="glass-card p-6" data-testid="food-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-white/20" />
                  <span className="text-white/30 text-xs tracking-widest uppercase">Food</span>
                </div>
                <button
                  onClick={() => setShowFoodInput(!showFoodInput)}
                  className="p-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-all"
                  data-testid="add-food-btn"
                >
                  <Plus className="w-4 h-4 text-white/30" />
                </button>
              </div>
              
              {/* Calorie Summary */}
              <div className="text-center mb-4">
                <span className="text-3xl font-light" style={{ color: sage }}>{totalCalories}</span>
                <span className="text-white/20 text-sm"> / {calorieGoal} cal</span>
                <p className={`text-xs mt-1 ${
                  calorieStatus === 'over' ? 'text-red-400/70' : 
                  calorieStatus === 'under' ? 'text-white/30' : 'text-green-400/70'
                }`}>
                  {calorieStatus === 'over' ? `${totalCalories - calorieGoal} over` : 
                   calorieStatus === 'under' ? `${calorieGoal - totalCalories} remaining` : 'Goal reached'}
                </p>
              </div>

              {/* Food Input Form - Simplified */}
              {showFoodInput && (
                <div className="space-y-3 mb-4 p-4 rounded-xl bg-white/[0.02] animate-fade-in">
                  <input
                    value={foodInput}
                    onChange={(e) => setFoodInput(e.target.value)}
                    placeholder="What did you eat? (e.g., 2 eggs, 1 cup rice)"
                    className="input-futuristic w-full text-sm"
                    data-testid="food-input"
                  />
                  <button
                    onClick={addFoodItem}
                    disabled={!foodInput.trim() || isEstimating}
                    className="btn-primary w-full text-sm py-2"
                    data-testid="log-food-btn"
                  >
                    {isEstimating ? 'Estimating...' : 'Log food'}
                  </button>
                </div>
              )}

              {/* Food Log List */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(todayData.foodLog || []).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02]"
                    data-testid={`food-item-${item.id}`}
                  >
                    <p className="text-white/60 text-sm flex-1 truncate">{item.food}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium" style={{ color: sage }}>{item.calories} cal</span>
                      <button
                        onClick={() => deleteFoodItem(item.id)}
                        className="p-1 rounded hover:bg-red-500/10 text-white/20 hover:text-red-400 transition-colors"
                        data-testid={`delete-food-${item.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* NIGHTTIME ROUTINE */}
            <section className="glass-card p-8" data-testid="nighttime-section">
              <h3 className="text-sm text-white/30 mb-6 tracking-widest uppercase">Nighttime routine</h3>
              
              <div className="space-y-2 mb-6">
                {[
                  { key: 'preppedClothes', label: 'Put out clothes for tomorrow' },
                  { key: 'brushedTeethPM', label: 'Brush teeth' },
                  { key: 'skinCarePM', label: 'Skincare' },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => updateToday({ [item.key]: !todayData[item.key] })}
                    className="flex items-center gap-4 p-4 rounded-xl w-full transition-all duration-300"
                    style={{
                      backgroundColor: todayData[item.key] ? sageBg : 'rgba(255,255,255,0.02)'
                    }}
                    data-testid={`night-${item.key}`}
                  >
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center transition-all"
                      style={{
                        backgroundColor: todayData[item.key] ? sage : 'transparent',
                        border: todayData[item.key] ? 'none' : '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      {todayData[item.key] && <Check className="w-3 h-3" style={{ color: '#0d0c0b' }} />}
                    </div>
                    <span className="text-sm font-light" style={{
                      color: todayData[item.key] ? sage : 'rgba(255,255,255,0.4)'
                    }}>
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
              
              <div>
                <label className="block text-white/30 text-xs mb-2 tracking-widest uppercase">Bedtime</label>
                <input
                  type="time"
                  value={todayData.bedTime || ''}
                  onChange={(e) => updateToday({ bedTime: e.target.value })}
                  className="input-futuristic w-full text-center"
                  data-testid="bedtime-input"
                />
              </div>
            </section>

            {/* DAY RATING */}
            <section className="glass-card p-8" data-testid="day-rating-section">
              <h3 className="text-sm text-white/30 mb-6 tracking-widest uppercase">How did today go?</h3>
              
              <div className="flex justify-center items-baseline mb-4">
                <span className="text-5xl font-light" style={{ color: sage }}>{todayData.dayRating || 5}</span>
                <span className="text-white/20 text-xl ml-1">/10</span>
              </div>
              
              <p className="text-center text-white/40 text-sm mb-6">
                {(todayData.dayRating || 5) <= 3 ? 'Rough day' : (todayData.dayRating || 5) <= 6 ? 'It was okay' : 'Solid day'}
              </p>
              
              <div className="relative mb-6 py-2">
                <div className="flex justify-between text-xs text-white/20 mb-2">
                  <span>Rough</span>
                  <span>Great</span>
                </div>
                <div className="relative h-8 flex items-center">
                  <div className="absolute inset-x-0 h-2 bg-white/[0.08] rounded-full"></div>
                  <div 
                    className="absolute h-2 rounded-full transition-all duration-200"
                    style={{ 
                      width: `${((todayData.dayRating || 5) - 1) / 9 * 100}%`,
                      backgroundColor: sage
                    }}
                  ></div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={todayData.dayRating || 5}
                    onChange={(e) => updateToday({ dayRating: parseInt(e.target.value) })}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                    data-testid="day-rating-slider"
                  />
                  <div 
                    className="absolute w-6 h-6 rounded-full border-2 border-white/20 transition-all duration-200 pointer-events-none"
                    style={{ 
                      left: `calc(${((todayData.dayRating || 5) - 1) / 9 * 100}% - 12px)`,
                      backgroundColor: sage
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-white/20 mt-2">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <span key={n} className={n === (todayData.dayRating || 5) ? 'text-white/50' : ''}>{n}</span>
                  ))}
                </div>
              </div>
            </section>

            {/* COMPLETION CELEBRATION */}
            {allHabitsComplete && !hasSeenCompletionCelebration && (
              <section className="glass-card p-10 text-center animate-fade-in" style={{ borderColor: 'rgba(138, 154, 138, 0.3)' }} data-testid="completion-celebration">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-8" style={{ backgroundColor: sageBg }}>
                  <Moon className="w-7 h-7" style={{ color: sage }} />
                </div>
                
                <p className="text-xl text-white/70 italic leading-relaxed font-light mb-8">
                  "{getCompletionQuote()}"
                </p>
                
                <p className="text-white/30 text-sm mb-8">
                  Time to wind down
                </p>
                
                <button
                  onClick={() => {
                    setHasSeenCompletionCelebration(true);
                    updateToday({ seenCompletionCelebration: true });
                  }}
                  className="btn-primary px-12"
                  data-testid="celebration-continue-btn"
                >
                  Evening routine
                </button>
              </section>
            )}

            {/* Progress */}
            <section className="glass-card p-8 text-center" data-testid="progress-section">
              <ProgressRing progress={completion} size={80} strokeWidth={4} color={sage} bgColor="rgba(255,255,255,0.03)">
                <span className="text-xl font-light" style={{ color: sage }}>{completion}%</span>
              </ProgressRing>
              <p className="text-white/20 text-xs mt-4 tracking-widest uppercase">Today</p>
            </section>

          </main>
        </div>
      )}
    </div>
  );
};

const MorningPage = () => {
  const { todayData, updateToday, settings } = useHabitData();
  
  return (
    <div className="min-h-screen pb-28 md:pb-8" data-testid="morning-page">
      <header className="pt-8 pb-6 px-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-400/20 flex items-center justify-center">
            <Sun className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Morning Intention</h1>
            <p className="text-white/50 text-sm">Start your day with purpose</p>
          </div>
        </div>
      </header>
      <main className="px-4 md:px-6 max-w-2xl mx-auto">
        <MorningIntention data={todayData} onUpdate={updateToday} settings={settings} />
      </main>
    </div>
  );
};

const HabitsPage = () => {
  const { todayData, updateToday, settings } = useHabitData();
  
  return (
    <div className="min-h-screen pb-28 md:pb-8" data-testid="habits-page">
      <header className="pt-8 pb-6 px-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-lime-400/20 flex items-center justify-center">
            <ListChecks className="w-6 h-6 text-lime-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Daily Habits</h1>
            <p className="text-white/50 text-sm">Track your daily wins</p>
          </div>
        </div>
      </header>
      <main className="px-4 md:px-6 max-w-2xl mx-auto">
        <DailyHabits data={todayData} settings={settings} onUpdate={updateToday} />
      </main>
    </div>
  );
};

const EveningPage = () => {
  const { todayData, updateToday } = useHabitData();
  
  return (
    <div className="min-h-screen pb-28 md:pb-8" data-testid="evening-page">
      <header className="pt-8 pb-6 px-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center">
            <Moon className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Evening Reflection</h1>
            <p className="text-white/50 text-sm">Reflect and grow</p>
          </div>
        </div>
      </header>
      <main className="px-4 md:px-6 max-w-2xl mx-auto">
        <EveningReflection data={todayData} onUpdate={updateToday} />
      </main>
    </div>
  );
};

const ReflectionsPage = () => {
  const { allData, settings, calculateCompletionPercentage } = useHabitData();
  
  return (
    <div className="min-h-screen pb-28 md:pb-8" data-testid="reflections-page">
      <header className="pt-8 pb-6 px-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(138, 154, 138, 0.2)' }}>
            <BookOpen className="w-6 h-6" style={{ color: '#8a9a8a' }} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Reflections</h1>
            <p className="text-white/50 text-sm">Your journey, your story</p>
          </div>
        </div>
      </header>
      <main className="px-4 md:px-6 max-w-2xl mx-auto">
        <Reflections 
          allData={allData}
          settings={settings}
          calculateCompletionPercentage={calculateCompletionPercentage}
        />
      </main>
    </div>
  );
};

const SettingsPage = () => {
  const { settings, setSettings } = useHabitData();
  const { requestNotificationPermission } = useReminders(settings, {});
  
  return (
    <div className="min-h-screen pb-28 md:pb-8" data-testid="settings-page">
      <header className="pt-8 pb-6 px-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Settings</h1>
            <p className="text-white/50 text-sm">Customize your experience</p>
          </div>
        </div>
      </header>
      <main className="px-4 md:px-6 max-w-2xl mx-auto">
        <Settings 
          settings={settings} 
          setSettings={setSettings}
          requestNotificationPermission={requestNotificationPermission}
        />
      </main>
    </div>
  );
};

// Mobile Bottom Navigation
const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const sage = '#8a9a8a';
  
  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Home' },
    { path: '/morning', icon: Sun, label: 'Morning' },
    { path: '/habits', icon: ListChecks, label: 'Habits' },
    { path: '/evening', icon: Moon, label: 'Evening' },
    { path: '/reflections', icon: BookOpen, label: 'Reflections' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings' },
  ];

  return (
    <nav className="mobile-nav md:hidden" data-testid="mobile-nav">
      {navItems.map(({ path, icon: Icon, label }) => (
        <button
          key={path}
          onClick={() => navigate(path)}
          className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all duration-300 ${
            location.pathname === path
              ? 'bg-white/[0.05]'
              : 'text-white/50 hover:text-white/80'
          }`}
          style={{
            color: location.pathname === path ? sage : undefined
          }}
          data-testid={`nav-${label.toLowerCase()}`}
        >
          <Icon className="w-5 h-5" />
          <span className="text-[10px] font-medium">{label}</span>
        </button>
      ))}
    </nav>
  );
};

// Desktop Sidebar Navigation
const DesktopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const sage = '#8a9a8a';
  
  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/morning', icon: Sun, label: 'Morning' },
    { path: '/habits', icon: ListChecks, label: 'Daily Habits' },
    { path: '/evening', icon: Moon, label: 'Evening' },
    { path: '/reflections', icon: BookOpen, label: 'Reflections' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings' },
  ];

  return (
    <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 flex-col bg-black/90 border-r border-white/10 p-6 z-40" data-testid="desktop-nav">
      <div className="flex items-center justify-center mb-10">
        <img 
          src={HYPHEN8_LOGO} 
          alt="Hyphen8" 
          className="h-12 object-contain"
        />
      </div>
      
      <nav className="flex-1 space-y-2">
        {navItems.map(({ path, icon: Icon, label }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all duration-300 ${
              location.pathname === path
                ? 'bg-white/[0.05] border border-white/[0.08]'
                : 'text-white/60 hover:bg-white/5 hover:text-white'
            }`}
            style={{
              color: location.pathname === path ? sage : undefined
            }}
            data-testid={`desktop-nav-${label.toLowerCase().replace(' ', '-')}`}
          >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{label}</span>
          </button>
        ))}
      </nav>
      
      <div className="mt-auto pt-6 border-t border-white/10">
        <p className="text-xs text-white/30 text-center">
          Built for self-improvement
        </p>
      </div>
    </aside>
  );
};

function AppContent() {
  return (
    <div className="md:ml-64">
      <DesktopNav />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/morning" element={<MorningPage />} />
        <Route path="/habits" element={<HabitsPage />} />
        <Route path="/evening" element={<EveningPage />} />
        <Route path="/reflections" element={<ReflectionsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
      <MobileNav />
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#050505]">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'white',
            backdropFilter: 'blur(10px)'
          }
        }}
      />
    </div>
  );
}

export default App;
